/**
 * LabeledEdgeBar.jsx
 * 
 * CANONICAL viz component - Single Source of Truth
 * Location: src/ui/src/components/viz/LabeledEdgeBar.jsx
 * 
 * Visualizes edge value with neutral colors (BSL compliant).
 * Handles negative values with bi-directional bar from center.
 * 
 * Props:
 * @param {string} label - Label text (e.g., "Edge", "Yds")
 * @param {number} value - Edge value (can be negative)
 * @param {number} maxEdge - Maximum edge for scaling (default: 50)
 */

import React from 'react';

const LabeledEdgeBar = ({ label, value, maxEdge = 50 }) => {
  const safeValue = value ?? 0;
  const isPositive = safeValue >= 0;
  
  // Cap visual bar at 100% of half-width
  const pct = Math.min(Math.abs(safeValue) / maxEdge * 100, 100);
  
  return (
    <div 
      className="flex items-center gap-2"
      title="Edge = Season Avg − Line"
    >
      {/* Label */}
      <span className="text-[10px] text-slate-500 w-10 shrink-0">
        {label}
      </span>
      
      {/* Bar container */}
      <div 
        className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden relative"
        title={`Edge: ${safeValue >= 0 ? '+' : ''}${safeValue.toFixed(1)} (Season Avg − Line)`}
      >
        {/* Center line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-300" />
        
        {/* Value bar - grows from center */}
        <div
          className={`absolute h-full rounded-full transition-all duration-200 ${
            isPositive ? 'bg-indigo-500' : 'bg-slate-400'
          }`}
          style={{
            width: `${pct / 2}%`,
            left: isPositive ? '50%' : `${50 - pct / 2}%`,
          }}
        />
      </div>
      
      {/* Value display */}
      <span className={`text-xs font-bold tabular-nums w-12 text-right ${
        isPositive ? 'text-indigo-600' : 'text-slate-500'
      }`}>
        {safeValue >= 0 ? '+' : ''}{safeValue.toFixed(1)}
      </span>
    </div>
  );
};

export default LabeledEdgeBar;